import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { Link, ExternalLink } from "lucide-react";

import { CREW_ROLES } from "../constants/constants";

import "./SubmissionList.css";

const SubmissionList = (props) => {
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const { loading, submissions } = props;

  // Format date to be more readable
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  // Format array fields to be more readable
  const formatArray = (array) => {
    if (!array || !Array.isArray(array)) return "None";
    return array.join(", ");
  };

  // Toggle expanded row
  const handleRowExpand = (row) => {
    setExpandedRows({
      ...expandedRows,
      [row._id]: !expandedRows[row._id],
    });
  };

  // Expanded row component
  const ExpandedComponent = ({ data }) => (
    <div className="expanded-row">
      <div className="expanded-section">
        <h3>Film Details</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Film Title:</span>
            <span className="detail-value">{data.filmTitle}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Borough:</span>
            <span className="detail-value">
              {data.borough || "Not specified"}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Director(s):</span>
            <span className="detail-value">{formatArray(data.directors)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">First Time Filmmaker:</span>
            <span className="detail-value">
              {data.isFirstTimeFilmmaker ? "Yes" : "No"}
            </span>
          </div>
        </div>
      </div>

      <div className="expanded-section">
        <h3>Cast & Crew</h3>
        <div className="detail-grid">
          {CREW_ROLES.map((role) => (
            <div className="detail-item" key={role.name}>
              <span className="detail-label">{role.shortLabel}:</span>
              <span className="detail-value">
                {formatArray(data[role.name])}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="expanded-section">
        <h3>Challenge Rules</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Challenge Code:</span>
            <span className="detail-value">{data.challengeCode}</span>
          </div>

          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Form Rule:</span>
              <span className="detail-value">
                {data.FormRuleInfo || "Not specified"}
              </span>
            </div>
          </div>
          <div className="detail-item">
            <span className="detail-label">Wildcard Rule:</span>
            <span className="detail-value">
              {data.WildCardInfo || "Not specified"}
            </span>
          </div>
        </div>
      </div>

      <div className="expanded-section">
        <h3>Additional Information</h3>
        <div className="detail-content">
          <p>{data.additionalInfo || "No additional information provided."}</p>
        </div>
      </div>

      <div className="expanded-section">
        <h3>Attendance</h3>
        <div className="detail-content">
          <p>Director will be present: {data.present || "Not specified"}</p>
        </div>
      </div>
    </div>
  );

  // Filter component
  const FilterComponent = () => (
    <div className="filter-container">
      <input
        type="text"
        placeholder="Search by film title, director..."
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        className="filter-input"
      />
      <button
        onClick={() => {
          setFilterText("");
          setResetPaginationToggle(!resetPaginationToggle);
        }}
        className="filter-clear-button"
      >
        Clear
      </button>
    </div>
  );

  // Filter function
  const filteredItems = submissions.filter((item) => {
    const directors = item.directors
      ? item.directors.join(" ").toLowerCase()
      : "";
    const searchText = filterText.toLowerCase();

    return (
      (item.filmTitle && item.filmTitle.toLowerCase().includes(searchText)) ||
      directors.includes(searchText) ||
      (item.borough && item.borough.toLowerCase().includes(searchText))
    );
  });

  const columns = [
    {
      name: "Film Title",
      selector: (row) => row.filmTitle || "Untitled",
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="film-title-cell">
          <span className="film-title">{row.filmTitle || "Untitled"}</span>
          {row.isFirstTimeFilmmaker && (
            <span className="debut-badge">Debut</span>
          )}
        </div>
      ),
    },
    {
      name: "Director(s)",
      selector: (row) => formatArray(row.directors),
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: (
        <span style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          Challenge Code
        </span>
      ),
      selector: (row) => row.challengeCode,
      sortable: true,
      width: "140px",
      hide: "sm",
    },
    {
      name: "Borough",
      selector: (row) => row.borough || "Not specified",
      sortable: true,
      width: "120px",
      hide: "sm",
    },
    {
      name: "Submitted",
      selector: (row) => row.timestamp,
      sortable: true,
      format: (row) => formatDate(row.timestamp),
      width: "180px",
      hide: "md",
      id: "timestamp",
    },
    {
      name: "Video",
      cell: (row) =>
        row.videoLink ? (
          <a
            href={row.videoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="video-link"
          >
            <ExternalLink size={16} />
            <span>Watch</span>
          </a>
        ) : (
          <span className="no-link">No link</span>
        ),
      width: "100px",
      center: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <button onClick={() => handleRowExpand(row)} className="details-button">
          {expandedRows[row._id] ? "Hide" : "Details"}
        </button>
      ),
      width: "100px",
      center: true,
      button: true,
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "#2c434d",
        color: "#d1c5b2",
      },
    },
    rows: {
      style: {
        backgroundColor: "#3a5361",
        color: "#d1c5b2",
        "&:hover": {
          backgroundColor: "#4a6371",
        },
      },
    },
    headRow: {
      style: {
        backgroundColor: "#2c434d",
        color: "#eb8876",
        fontWeight: "bold",
      },
    },
    headCells: {
      style: {
        color: "#eb8876",
        fontSize: "16px",
      },
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        color: "#d1c5b2",
      },
    },
    pagination: {
      style: {
        backgroundColor: "#2c434d",
        color: "#d1c5b2",
      },
      pageButtonsStyle: {
        color: "#eb8876",
        fill: "#eb8876",
        "&:disabled": {
          color: "#5f6d77",
          fill: "#5f6d77",
        },
      },
    },
  };

  return (
    <div className="submission-list-container">
      <FilterComponent />

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading submissions...</p>
        </div>
      ) : (
        <DataTable
          title="Film Submissions"
          columns={columns}
          data={filteredItems}
          pagination
          paginationResetDefaultPage={resetPaginationToggle}
          customStyles={customStyles}
          expandableRows
          expandableRowExpanded={(row) => expandedRows[row._id]}
          expandableRowsComponent={ExpandedComponent}
          highlightOnHover
          pointerOnHover
          persistTableHead
          defaultSortFieldId="timestamp"
          defaultSortAsc={false}
          noDataComponent={
            <div className="no-data-message">
              No submissions found. Please adjust your search criteria.
            </div>
          }
        />
      )}
    </div>
  );
};

export default SubmissionList;
